import {
	getList,
	isApiError,
	getSingle,
	post,
} from './utilities/provider';
import type { AxiosRequestConfig } from 'axios';
import {
	ExchangeSchoolSite,
	ExchangeSchoolSiteAthlete,
} from '@/types/exchangeSchoolSite';
import {
	SingleExchangeAthleteRateCard,
} from '@/types/exchangeAthletes';
import { type ApiV3 } from '@teamworksdev/influencer-core';


export function useExchangeSchoolSiteService(schoolSlug?: string, config?: Omit<AxiosRequestConfig, 'key'>) {
	const RESOURCE = `public/schools/${schoolSlug}`;
	const ATHLETE_RESOURCE = 'public/athletes';

	const getSchool = async () => {
		if (!schoolSlug) throw new Error('School identifier is required');
		const response = await getList<ApiV3.GetExchangeSchoolSiteResponse>(RESOURCE, {
			key: 'getExchangeSchoolSiteInformation',
			requiresAuth: false,
			requiresAcceptedTou: false,
			bypassErrorToast: true,
			...config,
		});
		if (isApiError(response)) throw response;
		return new ExchangeSchoolSite(response.data.data);
	};

	const getAthletes = async (params?: ApiV3.GetAthletesRequestParams) => {
		if (!schoolSlug) throw new Error('School identifier is required');
		const response = await getList<ApiV3.GetExchangeSchoolSiteAthletesResponse>(`${RESOURCE}/athletes`, {
			key: 'getExchangeSchoolSiteAthletes',
			requiresAuth: false,
			requiresAcceptedTou: false,
			params,
			...config,
		});
		if (isApiError(response)) throw response;
		const athletes = response.data.data.map((athlete) => new ExchangeSchoolSiteAthlete(athlete));
		const mappedResponse = {
			...response.data,
			data: athletes,
		};
		return mappedResponse;
	};

	const getAthleteRates = async (uuid: string) => {
		const response = await getSingle<ApiV3.GetSingleAthleteRatesResponse>(ATHLETE_RESOURCE, `${uuid}/rates`, {
			key: 'getExchangeSchoolSiteAthleteRates',
			requiresAuth: false,
			requiresAcceptedTou: false,
			...config,
		});
		if (isApiError(response)) throw response;
		return response.data.data.map((rateCard) => new SingleExchangeAthleteRateCard(rateCard));
	};

	const register = async (data: ApiV3.ExchangeRegisterRequestModel) => {
		if (!schoolSlug) throw new Error('School identifier is required');
		const response = await post<any>(`${RESOURCE}/exchange/register`, data, {
			key: 'createExchangeAccount',
			requiresAuth: false,
			requiresAcceptedTou: false,
			bypassErrorToast: true,
			...config,
		});
		if (isApiError(response)) throw response;
		return response;
	};

	const registerWithEmail = async (data: ApiV3.ExchangeRegisterWithEmailRequestModel) => {
		if (!schoolSlug) throw new Error('School identifier is required');
		const response = await post<any>(`${RESOURCE}/exchange/access`, data, {
			key: 'registerExchangeAccountWithEmail',
			requiresAuth: false,
			requiresAcceptedTou: false,
			bypassErrorToast: true,
			...config,
		});
		if (isApiError(response)) throw response;
		return response;
	};

	return {
		getSchool,
		getAthletes,
		getAthleteRates,
		register,
		registerWithEmail,
	};
}
